import { css } from '@emotion/react'
import { FormLabel, HFlow, SelectHelperMenuItem, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { CustomSelectFieldProps, resolveName, SelectField } from 'components/form/core'
import { HLabel } from 'components/HLabel'
import { InfoIcon } from 'components/InfoIcon'
import { Cid11SelectFieldDocument } from 'graphql/hooks.generated'
import { Cid11SelectFieldQuery, Cid11SelectFieldQueryVariables } from 'graphql/types.generated'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-final-form'

import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'
import { Cid11SelectFieldModel } from './model-cid11'

export type Cid11SelectFieldProps = CustomSelectFieldProps<Cid11SelectFieldModel> & {
  cid10Id: string | undefined
}

const DISABLED_TOOLTIP = 'É necessário informar CID-10 para habilitar o campo de CID-11'
const HEADER_TEXT = 'CID-11 relacionadas com a CID-10 selecionada'
const HINT_MESSAGE =
  'A CID-11 é a 11ª revisão da Classificação Internacional de Doenças, um novo padrão global da OMS para codificar e registrar doenças e condições de saúde.'
const EMPTY_ITEM_TEXT = 'Nenhum resultado encontrado.'
const NO_RELATION_AVAILABLE =
  'O CID-10 informado não possui um CID-11 correspondente. Devido à diferenças entre as versões da classificação, alguns códigos não apresentam equivalência direta.'

const createEmptyItem = (hasCid10: boolean, hasQuery: boolean) => () => {
  let text = ''

  if (hasCid10 && hasQuery) {
    text = EMPTY_ITEM_TEXT
  } else if (hasCid10 && !hasQuery) {
    text = NO_RELATION_AVAILABLE
  }

  return <SelectHelperMenuItem>{text}</SelectHelperMenuItem>
}

const extractItems = (data: Cid11SelectFieldQuery | null): Cid11SelectFieldModel[] =>
  data?.cids11?.content?.filter((item): item is Cid11SelectFieldModel => item != null) ?? []

const itemToString = (item: Cid11SelectFieldModel | null): string | null =>
  item ? `${item.nome.toUpperCase()} - ${item.codigo}` : null

const renderItem = (item: Cid11SelectFieldModel) => (
  <VFlow vSpacing={0}>
    <Text fontWeight='bold'>{item.nome.toUpperCase()}</Text>
    <HLabel title='Código '>{item.codigo}</HLabel>
  </VFlow>
)

function PrependHeader() {
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])
  return (
    <div css={styles.headerItem}>
      <Text fontWeight='bold' style={styles.headerItemText}>
        {HEADER_TEXT}
      </Text>
    </div>
  )
}

export function Cid11SelectField(props: Readonly<Cid11SelectFieldProps>) {
  const { cid10Id, disabled: disabledProp, label, onFilterChange: onFilterChangeProp, ...rest } = props

  const form = useForm()
  const [inputQuery, setInputQuery] = useState('')
  const hasCid10 = !!cid10Id
  const disabled = disabledProp || !hasCid10
  const resolvedName = resolveName(props.name)
  const prevCid10Id = useRef(cid10Id)

  useEffect(() => {
    if (prevCid10Id.current !== undefined && prevCid10Id.current !== cid10Id) {
      form.change(resolvedName, undefined)
      setInputQuery('')
    }
    prevCid10Id.current = cid10Id
  }, [cid10Id, form, resolvedName])

  const skip = useCallback(() => !hasCid10, [hasCid10])

  const variables = useCallback(
    (inputQuery: string): Cid11SelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        cid10Id: cid10Id ?? '',
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
      },
    }),
    [cid10Id]
  )

  const {
    selectProps: { onFilterChange: onFilterChangeBase, ...selectProps },
  } = useAsyncQuerySelect<Cid11SelectFieldModel, Cid11SelectFieldQuery, Cid11SelectFieldQueryVariables>({
    query: Cid11SelectFieldDocument,
    extractItems,
    variables,
    skip,
    debounceTime: 500,
  })

  const onFilterChange = useCallback<NonNullable<typeof onFilterChangeBase>>(
    (inputString, downshift) => {
      setInputQuery(inputString ?? '')
      onFilterChangeBase?.(inputString, downshift)
      onFilterChangeProp?.(inputString, downshift)
    },
    [onFilterChangeBase, onFilterChangeProp]
  )

  const hasQuery = !!inputQuery.trim()
  const components = useMemo(() => ({ EmptyItem: createEmptyItem(hasCid10, hasQuery), PrependItem: PrependHeader }), [
    hasCid10,
    hasQuery,
  ])

  return (
    <VFlow vSpacing={0.5}>
      {label && (
        <HFlow alignItems='center' hSpacing={0.25}>
          <FormLabel label={label} />
          <InfoIcon icon='infoCircleFilled' text={HINT_MESSAGE} />
        </HFlow>
      )}
      <Tooltip text={!hasCid10 ? DISABLED_TOOLTIP : undefined}>
        <SelectField<Cid11SelectFieldModel>
          itemToString={itemToString}
          renderItem={renderItem}
          components={components}
          disabled={disabled}
          onFilterChange={onFilterChange}
          {...selectProps}
          {...rest}
        />
      </Tooltip>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  headerItem: css`
    padding: 0.5rem 1rem;
    background-color: ${theme.pallete.primary.c90};
  `,
  headerItemText: css`
    color: ${theme.pallete.primary.c40};
  `,
})
